/**
 * ExpandSum Component
 *
 * This component renders footer content for the table when the **"Expand Sum"** mode
 * is active. It shows aggregated values for each column, and for the last column before currency
 * specifically, it provides a currency selector menu to allow switching between
 * different currency summaries.
 *
 * It supports dynamic merging of last column before CCY and CCY column, conditional rendering based
 * on the expand-sum state, and auto-closing of the currency menu via scroll/click-outside
 * behavior.
 *
 * Props:
 *  - `column` (string): The current table column key being rendered.
 *  - `isLastColumn` (boolean): Determines right-edge border behavior for merged cells.
 *  - `isFirstColumnAfterCCY` (boolean): Used to correctly apply borders to the first
 *    column after the CCY column when CCY is merged/hidden.
 *  - `className` (string): Additional CSS classes controlling visibility or layout
 *    (e.g., `"hidden_footer"`).
 *
 * State/Refs:
 *  - `shouldExpandCurrencies` (boolean): Controls whether the currency dropdown
 *    list is visible when expanding the sum inside last column before CCY.
 *
 *  - `data`: The aggregated footer value for the current column, returned by
 *    `useViewPerCurrency`.
 *
 *  - `selectedCurrency`: The currently selected currency from Redux.
 *  - `shouldExpandSum`: Global expand-sum mode flag from Redux. Determines whether
 *    the entire ExpandSum component renders anything at all.
 *
 *  - `menuRef`: A reference to the last column before CCY currency dropdown container.
 *
 * Derived Values:
 *  - `rowDensityClass`, `rowDensityHeight`: Layout utilities from `useRowDensity`
 *    controlling padding and height.
 *
 *  - `imgUrl`, `symbol`, `hasSpecialSymbol`: Parsed currency assets returned by
 *    `mapCCYAssets`, used to display icon + symbol in the last column before CCY expand-sum button.
 *
 * Behavior:
 *
 * 1. **Excluded Geo Columns**
 *    - When expand-sum mode is active, geo columns (except last column before CCY) should not
 *      display expanded values. These render an empty `FooterWrapper` with height
 *      aligned to row density.
 *
 * 2. **Merged last column before CCY + CCY Column**
 *    - When expand-sum mode is active, CCY column becomes visually merged into
 *      the last column before CCY footer cell.
 *    - last column before CCY footer shows:
 *        • A currency dropdown (`CurrencyList`)
 *        • A button showing the selected currency + icon
 *        • A toggle icon for expanding/collapsing the currency list
 *
 * 3. **Hidden CCY Column**
 *    - When expand-sum is active, CCY column is completely hidden (merged with LOCATION).
 *
 * 4. **Other Columns**
 *    - All non-geo, non-last column before CCY, non-CCY columns render their aggregated
 *      footer value (`data`) with appropriate borders.
 *
 * 5. **Auto-close Menu**
 *    - The currency dropdown automatically closes when clicking outside or
 *      scrolling the container (via `useCloseOnScrollAndClickOutside`).
 *
 * Example:
 *  <ExpandSum
 *    column="AMOUNT"
 *    isLastColumn={false}
 *    isFirstColumnAfterCCY={false}
 *    className="visible_footer"
 *  />
 */

import React, { useCallback, useRef } from 'react';
import { StatsTableColumn } from '../../../../../enum/TableColumn';
import {
  borderStyle,
  getLastColumnBeforeCurrency,
  getTableClass,
  isDeviceColumnExcludingLastBeforeCCY,
  isGeoColumnExcludingLastBeforeCCY,
} from '../../../../../util/table-helper';
import upArrow from '../../../../../assets/img/svg/table/up-arrow.svg';
import Button from '../../Button';
import CurrencyList from './CurrencyList/CurrencyList';
import { useAppSelector } from '../../../../../hook/useAppSelector';
import { useViewPerCurrency } from '../../../../../hook/useViewPerCurrency';
import { useRowDensity } from '../../../../../hook/useRowDensity';
import { mapCCYAssets } from '../../../../../util/currency';
import FooterWrapper from './FooterWrapper';
import { useAppDispatch } from '../../../../../hook/useAppDispatch';
import { setShouldViewCurrencies } from '../../../../../store/slice/stats-slice';
import { useOutsideFocus } from '../../../../../hook/useOutsideFocus';
import { numberFormatter } from '../../../../../util/numberFormatter';
import { usePageVisibleColumns } from '../../../../../hook/usePageVisibleColumns';
import { formatColumnValue } from '../../../../../util/formatColumnValue';

interface TableFooterProps {
  column: string;
  isLastColumn: boolean;
  isFirstColumnAfterCCY: boolean;
  className: string;
}

const ExpandSum: React.FC<TableFooterProps> = ({ column, isLastColumn, isFirstColumnAfterCCY, className }) => {
  const data = useViewPerCurrency(column);
  const { visibleColumns } = usePageVisibleColumns();

  const selectedCurrency = useAppSelector((state) => state.configSlice.selectedCurrency);
  const isMobile = useAppSelector((state) => state.configSlice.isMobile);
  const shouldViewCurrencies = useAppSelector((state) => state.stats.shouldViewCurrencies);
  const format = useAppSelector((state) => state.configSlice.format);
  const displayValue = formatColumnValue(column, data.toString(), format);

  const dispatch = useAppDispatch();

  const menuRef = useRef(null);
  const { rowDensityClass, rowDensityHeight } = useRowDensity();

  const { imgUrl, symbol, hasSpecialSymbol } = mapCCYAssets(selectedCurrency ?? '');

  const lastColumnBeforeCurrency = getLastColumnBeforeCurrency(
    visibleColumns.map((c) => c.name),
    (col) => col === StatsTableColumn.CCY,
  );

  const handleCloseCurrencies = useCallback(() => {
    dispatch(setShouldViewCurrencies(false));
  }, [dispatch]);

  useOutsideFocus(shouldViewCurrencies, handleCloseCurrencies, menuRef);

  // Skip excluded geo columns
  if (
    isGeoColumnExcludingLastBeforeCCY(column, visibleColumns) ||
    isDeviceColumnExcludingLastBeforeCCY(column, visibleColumns)
  ) {
    return (
      <FooterWrapper
        column={column}
        className={className}
        height={className === 'hidden_footer' ? 0 : rowDensityHeight}
        children={undefined}
      />
    );
  }

  // Render merged cell for last column before CCY (spanning last column before CCY + CCY)
  if (column === lastColumnBeforeCurrency) {
    return (
      <FooterWrapper column={column} className={className} refProp={menuRef}>
        <div className={`table_row ${getTableClass(column, 'footer_row', visibleColumns, isMobile)}`}>
          <div className={`table_cell ${rowDensityClass} last_geo_column`}>
            {shouldViewCurrencies && <CurrencyList />}
            <Button
              className={`button expand_sum ${rowDensityClass}`}
              icon={imgUrl}
              iconRight={upArrow}
              onClick={(e) => {
                e.stopPropagation();

                if (shouldViewCurrencies) {
                  dispatch(setShouldViewCurrencies(false));
                } else {
                  dispatch(setShouldViewCurrencies(true));
                }
              }}
            >
              {selectedCurrency} {hasSpecialSymbol && symbol}
            </Button>
          </div>
        </div>
      </FooterWrapper>
    );
  }

  // Skip CCY column (it’s merged with last column before CCY)
  if (column === StatsTableColumn.CCY) {
    return (
      <FooterWrapper
        column={column}
        className={className}
        height={className === 'hidden_footer' ? 0 : rowDensityHeight}
        children={undefined}
      />
    );
  }

  // Default for other columns
  return (
    <>
      <FooterWrapper column={column} className={className}>
        <div className={`table_row ${getTableClass(column, 'footer_row', visibleColumns, isMobile)}`}>
          <div
            className={`table_cell ${rowDensityClass}`}
            style={borderStyle({
              isFull: true,
              isLastColumn,
              isFirstColumnAfterCCY,
            })}
          >
            {displayValue}
          </div>
        </div>
      </FooterWrapper>
    </>
  );
};

export default ExpandSum;
